import type { SecretsEvent } from '@secretsoverflow/types';

// When is an event over, and when is it on "now"? The events feed gives
// resort-local dates, and the resort runs a nightly programme, so both questions
// are judged by the resort's calendar day rather than the visitor's clock. Kept
// in its own module so the rules are unit-tested away from the DOM.

/**
 * Today's date (YYYY-MM-DD) in the resort's own timezone, so "past" and
 * "happening now" flip over at the resort's midnight rather than the visitor's —
 * a guest browsing late at night in Los Angeles is already on the resort's next
 * day. Matches how the featured showcase computes its stay dates.
 */
export function resortToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(now);
}

/** The day after `day` (YYYY-MM-DD), by plain UTC date arithmetic (no timezone). */
export function addOneDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * The first day an event is no longer on — its exclusive end — as a resort-local
 * date. The feed already carries resort-local dates, so the YYYY-MM-DD prefix is
 * the resort day with no timezone math. Overnight events arrive as [day, day+1];
 * a single-day event as [day, day], which is pushed out by one so it still shows
 * on its own day. Null when there is no usable date, so the event is never judged
 * past on missing data.
 */
export function eventEndExclusive(e: SecretsEvent): string | null {
  const start = (e.startDate ?? '').slice(0, 10);
  const end = (e.endDate ?? e.startDate ?? '').slice(0, 10);
  if (!start && !end) return null;
  if (start && end && end > start) return end;
  return addOneDay(start || end);
}

/**
 * An event has ended once the resort's day reaches its exclusive end. This is why
 * a Tuesday-night event (stored [Tue, Wed]) drops on Wednesday instead of
 * lingering at the top of the list.
 */
export function eventIsPast(e: SecretsEvent, today: string): boolean {
  const endExclusive = eventEndExclusive(e);
  return endExclusive !== null && today >= endExclusive;
}

/**
 * An event is on "now" when the resort's day falls within [start, exclusive end).
 * Uses the same exclusive end as {@link eventIsPast}, so the "Happening Now" badge
 * and staying in the list always agree.
 */
export function eventIsHappeningNow(e: SecretsEvent, today: string): boolean {
  const start = (e.startDate ?? '').slice(0, 10);
  const endExclusive = eventEndExclusive(e);
  return Boolean(start && endExclusive && start <= today && today < endExclusive);
}
